import React, {ReactElement, useContext, useEffect, useState} from 'react';
import {NxButton, NxButtonVariant, NxLoader} from '@nextbank/ui-components';
import {Table, TableContainer} from '@material-ui/core';
import {Trans} from 'react-i18next';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import {getConsolidatedLoansTableHeaders} from '../../../../../../utils/table-helper';
import {CBSLoanDetails} from '../../../../../../shared/model/cbs-loans';
import {ConsolidatedLoansContext} from './consolidated-loans-context';
import {SetFieldValue} from '../../../../../../shared/model/formik.model';
import {CBS_LOANS} from '../../../../../../constants/api-urls';
import {ApiHelper} from '../../../../../../utils/api-helper';
import {StepContext} from '../loan-application-step/LoanApplicationStep';
import {WaiveInputContext} from './waive/waive-input-context';
import {WaiveInput, WaiveType} from '../../../../../../shared/model/consolidate-loans';
import useGet from '../../../../../../shared/hooks/use-get.hook';
import TableHeader from '../../../../../shared/table/header/TableHeaders';
import ConsolidatedLoansTableBody from './table/ConsolidatedLoansTableBody';
import AddConsolidatedLoansDialog from './dialog/AddConsolidatedLoansDialog';
import styles from './ConsolidatedLoans.module.scss';
import {AppSnackbarContext} from '../../../../../shared/app-snackbar-provider/AppSnackbarProvider';

interface Props {
  remadeFromLoanIds?: number[];
  waiveInputs?: WaiveInput[];
  setFieldValue: SetFieldValue;
  cbsLoanDetails?: CBSLoanDetails[];
  isConsolidatedLoansLoading?: boolean;
}

const HEADERS_TRANS_PREFIX = 'LOAN_CONFIGURATIONS.LOAN_APPLICATION_DATA.CONSOLIDATED_LOANS';

const ConsolidatedLoans = ({
                             remadeFromLoanIds,
                             waiveInputs: simulationWaiveInputs = [],
                             setFieldValue,
                             cbsLoanDetails = [],
                             isConsolidatedLoansLoading = false
                           }: Props): ReactElement => {

  const {isStepReadonly} = useContext(StepContext);
  const {showErrorMessage} = useContext(AppSnackbarContext);
  const [consolidatedLoans, setConsolidatedLoans] = useState<CBSLoanDetails[]>(cbsLoanDetails);
  const [waiveInputs, setWaiveInputs] = useState<WaiveInput[]>(simulationWaiveInputs);
  const columnHeaders = getConsolidatedLoansTableHeaders();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(isConsolidatedLoansLoading);
  const getCbsLoans = useGet<CBSLoanDetails[]>(CBS_LOANS);

  const handleClose = (): void => setOpenDialog(false);
  const getLoanIds = (cbsLoans: CBSLoanDetails[]): (number | undefined)[] => cbsLoans.map(loan => loan.loanId);

  const setFieldAndState = (cbsLoans: CBSLoanDetails[]): void => {
    setConsolidatedLoans(cbsLoans);
    setFieldValue('remadeFromLoanIds', getLoanIds(cbsLoans));
  };

  const addLoan = (loanDetail: CBSLoanDetails): void => {
    setFieldAndState([...consolidatedLoans, loanDetail]);
  };

  const removeLoan = (loanDetail: CBSLoanDetails): void => {
    setFieldAndState(consolidatedLoans.filter(loan => loan.loanId !== loanDetail.loanId));
  };

  const setWaiveInputsFieldAndState = (updatedWaiveInputs: WaiveInput[]): void => {
    setWaiveInputs(updatedWaiveInputs);
    setFieldValue('waiveInputs', updatedWaiveInputs);
  };

  const addWaiveInputs = (loanId: number, newWaiveInputs: WaiveInput[]): void => {
    const hasWaiveInput = waiveInputs.some(waive => waive.loanId === loanId);
    if (!hasWaiveInput) {
      setWaiveInputsFieldAndState([...waiveInputs, ...newWaiveInputs]);
    }
  };

  const removeWaiveInput = (loanId: number): void => {
    setWaiveInputsFieldAndState(waiveInputs.filter(waive => waive.loanId !== loanId));
  };

  const handleWaiveInputValueChange = (value: number | null | undefined,
                                       loanId?: number,
                                       waiveType?: WaiveType,
                                       feeDefitionId?: number): void => {
    if (isNil(waiveType)) {
      return;
    }
    const index = waiveInputs.findIndex(waive => {
      if (feeDefitionId) {
        return waive.loanId === loanId && waive.waiveType === waiveType && waive.feeDefinitionId === feeDefitionId;
      }
      return waive.loanId === loanId && waive.waiveType === waiveType;
    });

    if (index !== -1) {
      const updatedItemsCopy = [...waiveInputs];
      updatedItemsCopy[index].waiveAmount = !isNil(value) ? value : 0;
      setWaiveInputsFieldAndState(updatedItemsCopy);
    }
  };

  const fetchCbsLoans = (): void => {
    if (!isEmpty(remadeFromLoanIds) && isEmpty(cbsLoanDetails)) {
      setIsLoading(true);
      getCbsLoans(ApiHelper.constructUrlParams({loanIds: remadeFromLoanIds}))
        .then(result => setConsolidatedLoans(result))
        .catch(error => showErrorMessage(error.message))
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    fetchCbsLoans();
  }, []);

  return (
    <ConsolidatedLoansContext.Provider value={{
      consolidatedLoans: consolidatedLoans,
      addLoan: addLoan,
      removeLoan: removeLoan
    }}>
      <WaiveInputContext.Provider value={{
        waiveInputs,
        addWaiveInputs,
        removeWaiveInput,
        handleWaiveInputValueChange
      }}>
        <div className={styles.container}>
          <NxButton disabled={isStepReadonly} variant={NxButtonVariant.ADD} onClick={(): void => setOpenDialog(true)}>
            <Trans>{`${HEADERS_TRANS_PREFIX}.ADD_NEW`}</Trans>
          </NxButton>
        </div>
        <TableContainer>
          {(isLoading || isConsolidatedLoansLoading) ? <NxLoader /> :
            <>
              <Table>
                <TableHeader translationPath={HEADERS_TRANS_PREFIX} headers={columnHeaders} />
                <ConsolidatedLoansTableBody cbsLoans={consolidatedLoans} refetchCbsLoans={fetchCbsLoans} />
              </Table>
            </>
          }
        </TableContainer>
        <AddConsolidatedLoansDialog isVisible={openDialog} close={handleClose} />
      </WaiveInputContext.Provider>
    </ConsolidatedLoansContext.Provider>
  );
};

export default ConsolidatedLoans;
